<template>
  <div>
    <b-modal id="confirm-modal" :title="$t('application.documentList.confirmModal.title')" centered>
      <validation-observer ref="rules">
        <b-form ref="form" @submit.stop.prevent="handleSubmit">
          <b-form-group
            v-if="lääkehoitolupaExist"
            :label="$t('application.documentList.loveGranted.label')"
            label-for="lääkehoitolupaDocument"
          >
            <validation-provider #default="{ errors }" name="Love granted date" rules="required">
              <datepicker v-model="loveGranted" format="dd.MM.yyyy" input-class="form-control" />
              <small class="text-danger">{{
                errors[0] ? $t('application.documentList.loveGranted.errorMessage') : null
              }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="diplomaExist"
            :label="$t('application.documentList.graduation.label')"
            label-for="diplomaDocument"
          >
            <validation-provider #default="{ errors }" name="Graduation date" rules="required">
              <datepicker v-model="graduationDate" format="dd.MM.yyyy" input-class="form-control" />
              <small class="text-danger">{{
                errors[0] ? $t('application.documentList.graduation.errorMessage') : null
              }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="enrollmentExists"
            :label="$t('application.documentList.credits.label')"
            label-for="diplomaDocument"
          >
            <validation-provider #default="{ errors }" name="Credit points" rules="required">
              <b-form-input
                id="credits"
                v-model="creditPoints"
                placeholder="Opintopisteet"
                type="number"
              />
              <small class="text-danger">{{
                errors[0] ? $t('application.documentList.credits.errorMessage') : null
              }}</small>
            </validation-provider>
          </b-form-group>
          <p class="m-0">
            {{ $t('application.documentList.confirmModal.warningText') }}
          </p>
        </b-form>
      </validation-observer>

      <template #modal-footer="{cancel}">
        <b-button variant="outline-secondary" @click="cancel()">
          {{ $t('application.documentList.confirmModal.cancelTitle') }}
        </b-button>
        <b-button variant="info" @click.prevent="handleConfirmDocumentSelected">
          {{ $t('application.documentList.confirmModal.okTitle') }}
        </b-button>
      </template>
    </b-modal>

    <b-card>
      <template #header>
        <div class="d-flex justify-content-between align-items-center" style="width: 100%;">
          <h5 class="mb-0">
            {{ $t('application.documentList.title') }}
          </h5>
          <b-button
            v-if="confirmDocumentsShown"
            v-b-modal.confirm-modal
            variant="outline-primary"
            :disabled="selectedDocuments.length === 0"
          >
            {{ $t('application.documentList.confirmBtn') }}
          </b-button>
        </div>
      </template>
      <b-list-group
        v-if="documents.length > 0"
        class="d-flex justify-content-between align-items-center"
      >
        <b-list-group-item
          v-for="(document, index) in documents"
          :key="index"
          class="d-flex justify-content-between align-items-center"
          style="width: 100%;"
        >
          <b>
            {{ document.name }}
          </b>
          <div class="d-flex align-items-center">
            <div v-if="document.confirmed">
              <p style="font-weight: 500; color: #b9b9c3">
                {{ $t('documentRequests.warningText.confirmed') }}
              </p>
            </div>
            <b-form-checkbox
              v-if="!document.confirmed"
              v-model="document.selected"
              :disabled="document.confirmed"
              :value="true"
              :unchecked-value="false"
              @change="changeConfirmDocument(document)"
            />
            <b-button
              v-if="!document.confirmed"
              v-b-modal.remove-document-modal
              v-ripple.400="'rgba(234, 84, 85, 0.15)'"
              size="sm"
              variant="outline-danger"
              class="ml-1"
              @click="documentRemoveItem = document"
            >
              <feather-icon icon="XIcon" size="14" />
            </b-button>
          </div>
        </b-list-group-item>
      </b-list-group>
    </b-card>

    <b-modal
      id="remove-document-modal"
      :title="$t('application.deleteDocumentModal.title')"
      ok-variant="danger"
      @ok="removeDocument"
    >
      <p class="mt-1 d-flex justify-content-between">
        <span>
          {{ $t('application.deleteDocumentModal.content') }}
        </span>
      </p>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BListGroup,
  BListGroupItem,
  BForm,
  BFormGroup,
  BFormCheckbox,
  BModal,
  BFormInput,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import Datepicker from 'vuejs-datepicker'
import { ValidationProvider, ValidationObserver } from 'vee-validate'

export default {
  components: {
    BButton,
    BCard,
    BListGroup,
    BListGroupItem,
    BForm,
    BFormGroup,
    BFormCheckbox,
    BModal,
    BFormInput,
    Datepicker,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  props: {
    documents: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      documentRemoveItem: null,
      selectedDocuments: [],
      loveGranted: '',
      graduationDate: '',
      creditPoints: null,
    }
  },
  computed: {
    lääkehoitolupaExist() {
      return (
        this.selectedDocuments.includes('Lääkehoitolupa (teoria ja näytöt)') ||
        this.selectedDocuments.includes('Love')
      )
    },
    diplomaExist() {
      return (
        this.selectedDocuments.includes('Tutkintotodistus') ||
        this.selectedDocuments.includes('Tutkintatodistus')
      )
    },
    enrollmentExists() {
      let result = false
      const regExp = /(\s|^)?(opintosuorit)(\w+)?/g
      this.selectedDocuments.forEach(document => {
        if (document.toLowerCase().match(regExp)) {
          result = true
        }
      })
      return result
    },
    confirmDocumentsShown() {
      return this.documents && this.documents.filter(item => item.confirmed === false).length > 0
    },
  },
  methods: {
    changeConfirmDocument(document) {
      if (document.selected) {
        this.selectedDocuments.push(document.name)
      } else {
        this.selectedDocuments = this.selectedDocuments.filter(item => item !== document.name)
      }
    },
    handleConfirmDocumentSelected() {
      this.$refs.rules.validate().then(success => {
        if (success) {
          const data = {
            duration: 0,
            content: {
              message: this.selectedDocuments,
            },
          }
          if (this.lääkehoitolupaExist) {
            data.loveGranted = new Date(this.loveGranted)
          }
          if (this.diplomaExist && this.graduationDate) {
            data.graduationDate = new Date(this.graduationDate)
          }
          if (this.enrollmentExists && this.creditPoints) {
            data.creditPoints = parseInt(this.creditPoints, 10)
          }
          this.$emit('action', 'confirmed_documents', data)
          this.$bvModal.hide('confirm-modal')
        }
      })
    },
    removeDocument() {
      this.$emit('action', 'remove_documents', {
        duration: 0,
        content: {
          message: this.documentRemoveItem.name,
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import '@hw-ui/scss/vue/libs/vue-flatpicker.scss';
</style>
